import {
  AppliedFilters,
  dispatch,
  FilterRoute,
  FiltersPayload,
  ListFilterKey,
  ListFiltersMap,
  RelativeTimeRange,
  SortOptionsMap,
  TreeFilterKeys,
  TreeInputData,
  updateFiltersByRoute,
} from '@zonofi/zono-money-store';
import { isEmpty } from 'lodash';
import { SORT_FILTERS } from './sort-filters';
import { DEFINED_FILTERS } from './defined-filter';

export const hasSelectedSort = (sortFilter: SortOptionsMap): number => {
  const selectedSort = Object.values(sortFilter).find((x) => x.selected);
  return Number(Boolean(selectedSort));
};

export const countSelectedListFilter = (
  listFilter: Partial<ListFiltersMap>,
  item: ListFilterKey
): number => {
  const listFilterItems = listFilter?.[item]?.values || [];
  return listFilterItems.filter((x) => x.selected).length;
};

const countSelectedTreeValue = (filters: TreeInputData[]): number => {
  return filters.reduce((count, value) => {
    if (!value.data?.length && value.selected) {
      return count + 1;
    }
    if (value.data?.length) {
      return count + countSelectedTreeValue(value.data);
    }
    return count;
  }, 0);
};

export const countSelectedFilters = (
  filters: FiltersPayload,
  key: string
): number => {
  if (!filters) return 0;
  if (filters.listFilter && key in filters.listFilter) {
    const listItems = filters.listFilter[key as ListFilterKey]?.values || [];
    return listItems.filter((x) => x.selected).length;
  }
  if (filters.treeFilter && key in filters.treeFilter) {
    const tree = filters.treeFilter[key as TreeFilterKeys];
    if (!tree?.value) return 0;
    return countSelectedTreeValue(tree.value);
  }

  return 0;
};

export const hasAppliedFilters = (appliedFilters: AppliedFilters) => {
  return Object.values(appliedFilters).some((x) => !isEmpty(x));
};

const hasSelectedTreeValue = (filters: TreeInputData[]): boolean => {
  return filters.some((value) => {
    if (!value.data?.length && value.selected) {
      return true;
    }
    if (value.data?.length) {
      return hasSelectedTreeValue(value.data);
    }
    return false;
  });
};

export const isAnyFilterSelected = (filters: FiltersPayload): boolean => {
  if (!filters) return false;

  const listFilter = filters?.listFilter || {};
  const listSelected = Object.values(listFilter).some((filter) =>
    filter?.values?.some((item) => item.selected)
  );

  const sortOptions = filters?.sortOptions || {};
  const sortSelected = Object.values(sortOptions).some(
    (option) => option.selected
  );

  const treeFilter = filters?.treeFilter || {};
  const treeSelected = Object.values(treeFilter).some((tree) =>
    hasSelectedTreeValue(tree.value)
  );

  return listSelected || sortSelected || treeSelected;
};

export const initializeStaticFilters = (
  route: FilterRoute,
  filters: FiltersPayload
) => {
  const { sortOptions = {}, listFilter = {} } = filters;

  if (isEmpty(sortOptions) || isEmpty(listFilter)) {
    const sortOptions = SORT_FILTERS[route];
    const listFilter = DEFINED_FILTERS[route];

    dispatch(
      updateFiltersByRoute({
        loading: false,
        route,
        filter: { sortOptions, listFilter },
      })
    );
  }
};

const RANGE_TO_DAYS: Record<RelativeTimeRange, number> = {
  LAST_24_HOURS: 1,
  LAST_3_DAYS: 3,
  LAST_7_DAYS: 7,
  LAST_15_DAYS: 15,
  LAST_30_DAYS: 30,
  LAST_60_DAYS: 60,
};

export const getDateRangeFromTimeRange = (range: RelativeTimeRange) => {
  const now = new Date();
  const days = RANGE_TO_DAYS[range];
  const startTime = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  return { startTime, endTime: now };
};

export const getIsoWithMicroseconds = (date: Date): string => {
  return date.toISOString().replace(/\.(\d{3})Z$/, '.$1000Z');
};
